/**
 * Совместная правка заметки (DEV-1974).
 *
 * На встрече в одну карточку пишут вдвоём, и «кто последний сохранил, того и
 * текст» здесь не годится: правка соседа обязана лечь рядом с твоей, а не
 * поверх. Слияние считает Yjs — писать своё значит месяцами ловить случаи,
 * которые в ней уже пойманы.
 *
 * Транспорт свой: вниз — тот же поток встречи, что носит остальные её события,
 * вверх — короткий POST раз в тик. Вебсокет не заводим ради этого: поток уже
 * есть, проходит ingress и переживает обрывы, а правки текста — это десятки
 * байт, которым отдельное соединение ничего не даёт.
 *
 * Присутствие (каретки, выделения, «печатает заметку…») ходит тем же путём и
 * нигде не хранится: курсор минутной давности никому не нужен.
 */
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate } from 'y-protocols/awareness'
import * as Y from 'yjs'

import { ensureFreshToken } from '@/api/http'

const baseURL = import.meta.env.VITE_API_BASE_URL || ''

/**
 * Пауза перед отправкой накопленных правок.
 *
 * Символ за символом — это запрос на каждое нажатие; тик в 150 мс склеивает
 * набранное слово в одну правку и остаётся незаметным на глаз.
 */
const FLUSH_MS = 150

/** Пауза перед отправкой присутствия: курсор двигают чаще, чем его видно */
const PRESENCE_MS = 120

/**
 * Тишина, после которой состояние заметки закрепляется снапшотом.
 *
 * Пока снапшота нет, остальная система видит прежний текст: лента, поиск и
 * разбор в задачи читают body_md. Три секунды — пауза между мыслями на
 * созвоне, а не между словами.
 */
const SNAPSHOT_MS = 3_000

/** Метка своих правок: их обратно на сервер не отправляют */
const REMOTE = 'remote'

const toBase64 = (bytes: Uint8Array): string => {
  let binary = ''

  // Порциями, а не одним `String.fromCharCode(...bytes)`: длинный снапшот
  // разворачивается в аргументы и переполняет стек вызовов.
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000))
  }

  return btoa(binary)
}

const fromBase64 = (value: string): Uint8Array => {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  return bytes
}

const authHeaders = async (): Promise<HeadersInit> => ({
  Authorization: `Bearer ${await ensureFreshToken()}`,
  'Content-Type': 'application/json',
})

interface NoteStateResponse {
  snapshot?: string
  snapshot_seq?: number
  updates?: { seq: number; actor_id: string; payload: string }[]
  body_md?: string
}

/** Состояние заметки: снапшот и хвост журнала после указанного номера */
const readNoteState = async (noteId: string, after: number): Promise<NoteStateResponse> => {
  const response = await fetch(`${baseURL}/api/v1/tracker/notes/${noteId}/updates?after=${after}`, {
    headers: await authHeaders(),
  })

  if (!response.ok) throw new Error(`note updates: ${response.status}`)

  return (await response.json()) as NoteStateResponse
}

/** Отправить правку; в ответ приходит её номер в журнале */
const sendNoteUpdate = async (noteId: string, update: Uint8Array): Promise<number> => {
  const response = await fetch(`${baseURL}/api/v1/tracker/notes/${noteId}/updates`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ update: toBase64(update) }),
  })

  if (!response.ok) throw new Error(`note update: ${response.status}`)

  const body = (await response.json()) as { seq?: number }

  return body.seq ?? 0
}

/** Закрепить состояние заметки вместе с обычным текстом */
const sendNoteSnapshot = async (
  noteId: string,
  snapshot: Uint8Array,
  bodyMD: string,
  seq: number,
): Promise<void> => {
  const response = await fetch(`${baseURL}/api/v1/tracker/notes/${noteId}/snapshot`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ snapshot: toBase64(snapshot), body_md: bodyMD, seq }),
  })

  if (!response.ok) throw new Error(`note snapshot: ${response.status}`)
}

const sendAwareness = async (sessionId: string, payload: Uint8Array): Promise<void> => {
  await fetch(`${baseURL}/api/v1/tracker/plan/meeting/${sessionId}/awareness`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ payload: toBase64(payload) }),
  })
}

/**
 * Живой документ одной заметки.
 *
 * Держит текст, копит свои правки и отдаёт их пачкой, принимает чужие и сам
 * догоняет пропущенное: номера в журнале сквозные, и разрыв в них означает,
 * что пачка прошла мимо, пока лежала сеть.
 */
export class NoteDoc {
  readonly doc = new Y.Doc()
  readonly text: Y.Text

  /** Последний применённый номер журнала */
  private seq = 0
  private queue: Uint8Array[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private snapshotTimer: ReturnType<typeof setTimeout> | null = null
  private closed = false
  private sending = false

  /**
   * Не удалось отправить накопленное — правки лежат у нас и уйдут следующей
   * попыткой. Экран показывает это жёлтой плашкой, а не молчит.
   */
  isPending = false

  constructor(
    readonly noteId: string,
    private readonly onChange: () => void,
  ) {
    this.text = this.doc.getText('body')
  }

  /**
   * Поднять документ.
   *
   * Пустой документ у заметки, написанной до совместной правки, — обычное
   * дело: текст в ней лежит обычной строкой. Первый, кто открыл её на правку,
   * и переносит текст в документ.
   *
   * ponytail: два человека, открывшие такую заметку в одну и ту же секунду,
   * получат текст дважды. Лечится инициализацией на сервере — но там нет и не
   * будет реализации CRDT, а вероятность совпадения меньше, чем цена второй
   * реализации слияния на Go.
   */
  async open(fallbackBody: string): Promise<void> {
    const state = await readNoteState(this.noteId, 0)

    this.applyState(state)

    if (!this.text.length && fallbackBody) {
      this.text.insert(0, fallbackBody)
    }

    this.doc.on('update', this.onLocalUpdate)
  }

  /** Применить чужую правку из потока встречи */
  applyRemote(payload: string, seq: number): void {
    if (this.closed) return

    Y.applyUpdate(this.doc, fromBase64(payload), REMOTE)

    // Пропуск в нумерации означает потерянную пачку: дочитываем журнал, иначе
    // текст разъедется тихо — и тем незаметнее, чем длиннее протокол.
    if (seq > this.seq + 1) {
      void this.catchUp()
    }

    this.seq = Math.max(this.seq, seq)
    this.onChange()
  }

  /** Догнать пропущенное: правки после последнего применённого номера */
  async catchUp(): Promise<void> {
    if (this.closed) return

    const state = await readNoteState(this.noteId, this.seq)

    this.applyState(state)
    this.onChange()
  }

  /** Отдать накопленные правки */
  async flush(): Promise<void> {
    if (this.closed || this.sending || !this.queue.length) return

    const batch = this.queue

    this.queue = []
    this.sending = true

    try {
      const seq = await sendNoteUpdate(this.noteId, Y.mergeUpdates(batch))

      this.seq = Math.max(this.seq, seq)
      this.isPending = false
    } catch {
      // Сеть отвалилась — правки возвращаются в очередь и уйдут следующим
      // тиком. Терять их нельзя: на созвоне это единственная копия сказанного.
      this.queue = [...batch, ...this.queue]
      this.isPending = true
    } finally {
      this.sending = false
      this.onChange()
    }
  }

  /**
   * Закрепить состояние: снапшот и обычный текст.
   *
   * Текст уходит вместе со снапшотом, потому что читает его вся остальная
   * система — лента, поиск, разбор в задачи. Заметка, написанная вдвоём,
   * обязана выглядеть в них так же, как написанная одним человеком.
   */
  async persist(): Promise<void> {
    await this.flush()

    if (this.closed || !this.text.length) return

    try {
      await sendNoteSnapshot(
        this.noteId,
        Y.encodeStateAsUpdate(this.doc),
        this.text.toString(),
        this.seq,
      )
    } catch {
      // Снапшот — оптимизация, а не сохранение: сами правки уже в журнале, и
      // следующая попытка закрепит состояние целиком.
      this.isPending = true
      this.onChange()
    }
  }

  destroy(): void {
    this.closed = true

    if (this.timer) clearTimeout(this.timer)
    if (this.snapshotTimer) clearTimeout(this.snapshotTimer)

    this.doc.off('update', this.onLocalUpdate)
    this.doc.destroy()
  }

  private applyState(state: NoteStateResponse): void {
    if (state.snapshot) {
      Y.applyUpdate(this.doc, fromBase64(state.snapshot), REMOTE)
      this.seq = Math.max(this.seq, state.snapshot_seq ?? 0)
    }

    for (const update of state.updates ?? []) {
      Y.applyUpdate(this.doc, fromBase64(update.payload), REMOTE)
      this.seq = Math.max(this.seq, update.seq)
    }
  }

  private onLocalUpdate = (update: Uint8Array, origin: unknown) => {
    this.onChange()

    // Своё возвращаем на сервер, чужое — уже оттуда.
    if (origin === REMOTE || this.closed) return

    this.queue.push(update)

    if (this.timer) return

    this.timer = setTimeout(() => {
      this.timer = null
      void this.flush()
    }, FLUSH_MS)

    if (this.snapshotTimer) clearTimeout(this.snapshotTimer)

    this.snapshotTimer = setTimeout(() => {
      this.snapshotTimer = null
      void this.persist()
    }, SNAPSHOT_MS)
  }
}

/** Что сосед делает прямо сейчас */
export interface PeerState {
  userId: string
  name: string
  /** Заметка, которую он правит; пусто — просто присутствует */
  noteId: string
  /** Где стоит его каретка и что выделено */
  from: number
  to: number
  /** Печатает в композере — новая заметка, которой ещё нет */
  typing: boolean
}

/**
 * Присутствие на встрече: кто что правит и где стоит его каретка.
 *
 * Живёт на своём пустом документе: правки текста и присутствие расходятся
 * разными дорогами — первые обязаны пережить обрыв, вторые бессмысленны через
 * секунду после того, как случились.
 */
export class MeetingPresence {
  readonly awareness: Awareness

  private readonly doc = new Y.Doc()
  private timer: ReturnType<typeof setTimeout> | null = null
  private closed = false

  constructor(
    private readonly sessionId: string,
    private readonly me: { userId: string; name: string },
    private readonly onChange: () => void,
  ) {
    this.awareness = new Awareness(this.doc)
    this.awareness.setLocalState({ user: this.me, noteId: '', from: 0, to: 0, typing: false })
    this.awareness.on('update', this.onAwarenessUpdate)
  }

  /** Сказать остальным, где мы и что делаем */
  setLocal(state: { noteId?: string; from?: number; to?: number; typing?: boolean }): void {
    if (this.closed) return

    const current = this.awareness.getLocalState() ?? {}

    this.awareness.setLocalState({ ...current, user: this.me, ...state })
  }

  /** Применить чужое присутствие из потока */
  applyRemote(payload: string): void {
    if (this.closed) return

    applyAwarenessUpdate(this.awareness, fromBase64(payload), REMOTE)
    this.onChange()
  }

  /** Все, кроме себя */
  peers(): PeerState[] {
    const out: PeerState[] = []

    for (const [clientId, state] of this.awareness.getStates()) {
      if (clientId === this.awareness.clientID) continue

      const peer = state as {
        user?: { userId?: string; name?: string }
        noteId?: string
        from?: number
        to?: number
        typing?: boolean
      }

      if (!peer.user?.userId) continue

      out.push({
        userId: peer.user.userId,
        name: peer.user.name ?? '',
        noteId: peer.noteId ?? '',
        from: peer.from ?? 0,
        to: peer.to ?? 0,
        typing: peer.typing ?? false,
      })
    }

    return out
  }

  /**
   * Забыть чужое присутствие.
   *
   * Зовётся на обрыве потока: пока связи нет, чужие каретки не двигаются, и
   * оставлять их на экране значит показывать, что сосед замер на середине
   * слова, — хотя на самом деле это мы его не слышим.
   */
  forgetPeers(): void {
    for (const clientId of this.awareness.getStates().keys()) {
      if (clientId !== this.awareness.clientID) this.awareness.getStates().delete(clientId)
    }

    this.onChange()
  }

  destroy(): void {
    this.closed = true

    if (this.timer) clearTimeout(this.timer)

    this.awareness.off('update', this.onAwarenessUpdate)
    this.awareness.destroy()
    this.doc.destroy()
  }

  private onAwarenessUpdate = (
    changes: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown,
  ) => {
    this.onChange()

    if (origin === REMOTE || this.closed) return

    // Своё состояние отправляем не чаще, чем его успевают увидеть: курсор
    // двигают куда чаще, чем на него смотрят.
    if (this.timer) return

    this.timer = setTimeout(() => {
      this.timer = null

      const payload = encodeAwarenessUpdate(this.awareness, [this.awareness.clientID])

      // Отказ здесь молчит намеренно: присутствие — не данные, следующий
      // тик отправит его заново.
      void sendAwareness(this.sessionId, payload).catch(() => undefined)
    }, PRESENCE_MS)

    void changes
  }
}

/** Длина общего начала двух строк */
export const commonPrefix = (left: string, right: string): number => {
  const limit = Math.min(left.length, right.length)
  let index = 0

  while (index < limit && left[index] === right[index]) index += 1

  return index
}

/**
 * Перенести правку поля в документ куском, а не заменой текста целиком.
 *
 * Замена выглядела бы для соседа как «всё стёрли и написали заново»: его
 * каретка улетела бы в конец, а сливать стало бы нечего — в документе не
 * осталось бы ни одного прежнего символа.
 */
export const applyTextDiff = (text: Y.Text, next: string): void => {
  const before = text.toString()

  if (before === next) return

  const start = commonPrefix(before, next)
  let tail = 0

  while (
    tail < before.length - start &&
    tail < next.length - start &&
    before[before.length - 1 - tail] === next[next.length - 1 - tail]
  ) {
    tail += 1
  }

  const removed = before.length - start - tail
  const added = next.slice(start, next.length - tail)

  text.doc?.transact(() => {
    if (removed > 0) text.delete(start, removed)
    if (added) text.insert(start, added)
  })
}

/** Цвет соавтора: он же красит его каретку, выделение и чип в шапке */
const PEER_COLORS = ['#7C5CFF', '#0EA5E9', '#F59E0B', '#EC4899', '#10B981', '#F43F5E']

export const peerColor = (userId: string): string => {
  let hash = 0

  for (let index = 0; index < userId.length; index += 1) {
    hash = (hash * 31 + userId.charCodeAt(index)) % 997
  }

  return PEER_COLORS[hash % PEER_COLORS.length] as string
}
